import {closeCurrentDialog} from 'platform/components';

import {isNotNil, isNumber, isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  TestDriveResponseBody,
  useCreateTestDriveMutation,
  useGetBusinessCaseQuery,
  useUpdateTestDriveMutation,
} from '@dms/api/sales';
import {castTimeToDateISOString} from '@dms/features/tasks';
import {i18n} from '@dms/i18n';
import {getCustomerName, handleApiError, useUpdateOdometer} from '@dms/shared';

import {ApiError, isNotNilOrEmpty} from 'shared/web';

import {TestDriveDialogSchemaType} from '../TestDriveDialogSchema';
import {getMileageUpdateCommand} from '../utils/getMileageUpdateCommands';

interface UseTestDriveMileageSubmitProps {
  businessCaseId: string;
  selectedVehicleId: string | null;
  testDrive?: TestDriveResponseBody;
}

const buildTestDriveRequestBody = (formData: TestDriveDialogSchemaType) => ({
  userId: formData.userId,
  state: formData.state,
  note: formData.note,
  scheduledAt: castTimeToDateISOString(formData.scheduledAtDate, formData.scheduledAtTime),
  fuelBefore:
    isNotNil(formData.fuelBefore) && isNumber(formData.fuelBefore)
      ? formData.fuelBefore * 100
      : null,
  fuelAfter:
    isNotNil(formData.fuelAfter) && isNumber(formData.fuelAfter) ? formData.fuelAfter * 100 : null,
  mileageBefore: formData.mileageBefore,
  mileageAfter: formData.mileageAfter,
  internalNote: formData.internalNote,
  customerFeedback: formData.customerFeedback,
  customerName: formData.customerName ?? null,
  customerDriversLicence: formData.customerDriversLicence ?? null,
  vehicleId: formData.vehicleId,
  returnedAt:
    formData.returnedAtDate && formData.returnedAtTime
      ? castTimeToDateISOString(formData.returnedAtDate, formData.returnedAtTime)
      : null,
});

export const useTestDriveMileageSubmit = (props: UseTestDriveMileageSubmitProps) => {
  const [createTestDrive] = useCreateTestDriveMutation();
  const [updateTestDrive] = useUpdateTestDriveMutation();
  const [updateOdometer] = useUpdateOdometer(props.selectedVehicleId);

  const {data: businessCase} = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});
  const {data: customer} = useGetCustomerV2Query(
    {customerId: businessCase?.customerId ?? ''},
    {skip: isNilOrEmpty(businessCase?.customerId)}
  );

  const saveEditedTestDrive = async (
    values: ReturnType<typeof buildTestDriveRequestBody>
  ): Promise<string | null> => {
    if (!isNotNilOrEmpty(props.testDrive?.id)) {
      return null;
    }

    return await updateTestDrive({
      testDriveId: props.testDrive.id,
      businessCaseId: props.businessCaseId,
      updateTestDriveRequestBody: values,
    })
      .unwrap()
      .then(() => props.testDrive?.id ?? null)
      .catch((error: ApiError) => {
        handleApiError(error);
        return null;
      });
  };

  const saveCreatedTestDrive = async (
    values: ReturnType<typeof buildTestDriveRequestBody>
  ): Promise<string | null> =>
    await createTestDrive({
      businessCaseId: props.businessCaseId,
      createTestDriveRequestBody: values,
    })
      .unwrap()
      .then((response) => response.id)
      .catch((error: ApiError) => {
        handleApiError(error);
        return null;
      });

  const handleMileageUpdate = async (
    recordId: string,
    values: ReturnType<typeof buildTestDriveRequestBody>
  ) => {
    if (!props.selectedVehicleId) {
      return;
    }

    const recordTitle = i18n.t('page.businessCase.title', {name: getCustomerName(customer)});

    const command = getMileageUpdateCommand({
      recordId,
      previousValues: {
        mileageBefore: props.testDrive?.mileageBefore,
        mileageAfter: props.testDrive?.mileageAfter,
      },
      submittedValues: {
        mileageBefore: values.mileageBefore,
        mileageAfter: values.mileageAfter,
      },
    });

    if (command) {
      await updateOdometer({...command, recordTitle});
    }
  };

  const handleSubmit = async (formData: TestDriveDialogSchemaType) => {
    const values = buildTestDriveRequestBody(formData);

    const recordId = isNotNilOrEmpty(props.testDrive?.id)
      ? await saveEditedTestDrive(values)
      : await saveCreatedTestDrive(values);

    if (!recordId) {
      return;
    }

    closeCurrentDialog();

    await handleMileageUpdate(props.businessCaseId, values);
  };

  return {handleSubmit};
};
